/**
 * @file useCertificateFile.ts
 * @description Файл сертификата в окне сертификата (phase19.16, партия
 * «Юридические документы», шаг В; SB141; uiux-legal-steps-b-c §6.3):
 * выбранный, но ещё не отправленный файл нового сертификата, загрузка с
 * процентом, замена сразу у сохранённого («Новый файл сразу заменит
 * прежний на сайте»), «Убрать» и «Открыть», текст ошибки по коду.
 *
 * Сохранённый сертификат (`saved`) — ответ сервера: после загрузки и
 * удаления файла окно показывает то, что вернул API, а не правит руками.
 */

import { useCallback, useState } from "react";

import { useNotify } from "../../components/ui/NotifyProvider";
import { catalogProducerCertificates, type ProducerCertificate } from "../../lib/api-catalog";
import { ApiError } from "../../lib/api-client";
import { certificateFileIssue, certificateUploadErrorText } from "../../lib/certificate-status";
import { useOpenFileInTab } from "../useOpenFileInTab";

export interface CertificateFileApi {
  /** Выбран, но не отправлен (новый сертификат до «Сохранить»). */
  pending: File | null;
  /** Процент загрузки; null — не грузим. */
  progress: number | null;
  /** Ошибка выбора или загрузки словами. */
  error: string | null;
  /** Файл выбран: у сохранённого — сразу загрузка, у нового — ждёт «Сохранить». */
  pick: (file: File) => void;
  /** Загрузить файл в сертификат; true — загружен. */
  upload: (cert: ProducerCertificate, file: File) => Promise<boolean>;
  remove: (cert: ProducerCertificate) => Promise<void>;
  /** Открыть файл в новой вкладке (вкладка — синхронно по нажатию). */
  open: (cert: ProducerCertificate) => void;
  clearPending: () => void;
}

/** `saved` — сертификат на сервере (null — новый); `onSaved` — ответ сервера после правки файла. */
export function useCertificateFile(producerId: string, saved: ProducerCertificate | null, onSaved: (cert: ProducerCertificate) => void): CertificateFileApi {
  const notify = useNotify();
  const [pending, setPending] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const openInTab = useOpenFileInTab();

  const upload = useCallback(
    async (cert: ProducerCertificate, file: File) => {
      setError(null);
      setProgress(0);
      try {
        onSaved(await catalogProducerCertificates.uploadFile(producerId, cert.id, file, setProgress));
        setPending(null);
        return true;
      } catch (err) {
        setError(err instanceof ApiError ? certificateUploadErrorText(err.code, err.message) : "Не удалось загрузить файл");
        return false;
      } finally {
        setProgress(null);
      }
    },
    [producerId, onSaved],
  );

  const pick = useCallback(
    (file: File) => {
      const issue = certificateFileIssue(file);
      if (issue) return setError(issue);
      setError(null);
      if (saved) void upload(saved, file);
      else setPending(file);
    },
    [saved, upload],
  );

  const remove = useCallback(
    async (cert: ProducerCertificate) => {
      try {
        onSaved(await catalogProducerCertificates.removeFile(producerId, cert.id));
        notify.success("Файл убран");
      } catch (err) {
        notify.error(err instanceof ApiError ? err.message : "Не удалось убрать файл. Попробуйте ещё раз.");
      }
    },
    [producerId, onSaved, notify],
  );

  const open = useCallback(
    (cert: ProducerCertificate) => openInTab(() => catalogProducerCertificates.downloadFile(producerId, cert.id)),
    [producerId, openInTab],
  );

  return { pending, progress, error, pick, upload, remove, open, clearPending: () => setPending(null) };
}
